/**
 * GTD Domain Rules
 * 
 * Domain-specific validation rules following David Allen's Getting Things Done methodology.
 * Owned and maintained by the productivity and task management domain experts.
 * 
 * Follows Eric Evans DDD principle: each domain defines its own rules.
 */

// TODO: These types need to be defined or imported from the correct package
// Temporarily commenting out until the proper types are available
/*
import type { 
  DomainRulesContract,
  NamingRule,
  IntegrationRule,
  ArchitecturalRule,
  DomainCapability,
  DomainEventMetadata
} from '@swoft/core';
*/

// Temporary type definitions to allow compilation
type DomainRulesContract = any;
type NamingRule = any;
type IntegrationRule = any;
type ArchitecturalRule = any;
type DomainCapability = any;
type DomainEventMetadata = any;

export class GTDDomainRules implements DomainRulesContract {
  readonly domainName = 'gtd-domain';
  readonly version = '1.0.0';
  readonly description = 'Domain rules for Getting Things Done productivity system by David Allen';
  readonly maintainer = 'Productivity Systems Team';

  readonly namingRules: readonly NamingRule[] = [
    {
      pattern: 'entity',
      examples: ['InboxItem', 'Project', 'NextAction', 'SomedayMaybeItem', 'Reference'],
      description: 'GTD entities represent core productivity concepts from David Allen methodology'
    },
    {
      pattern: 'value-object',
      examples: ['Context', 'Priority', 'EnergyLevel', 'ProcessingStatus', 'ActionableDecision'],
      description: 'Value objects capture GTD workflow attributes and decision points'
    },
    {
      pattern: 'command',
      examples: ['CaptureInboxItem', 'ClarifyItem', 'OrganizeAction', 'CompleteTask', 'ProcessInbox'],
      description: 'GTD commands follow David Allen workflow verbs - capture, clarify, organize, reflect, engage'
    },
    {
      pattern: 'event',
      examples: ['ItemCaptured', 'ItemClarified', 'ProjectCompleted', 'ActionCreated', 'ItemOrganized'],
      description: 'GTD events represent completed workflow steps - always past tense (facts that happened)'
    }
  ];

  readonly integrationRules: readonly IntegrationRule[] = [
    {
      type: 'published-language',
      description: 'Provides GTD workflow capabilities to AI agents via Published Language contracts',
      allowedIntegrations: ['ai-domain'],
      restrictedIntegrations: []
    },
    {
      type: 'event-driven',
      description: 'Publishes productivity events for person activity tracking',
      allowedIntegrations: ['party-manager'],
      restrictedIntegrations: []
    },
    {
      type: 'foreign-key',
      description: 'References persons and organizations via IDs without direct coupling',
      allowedIntegrations: ['party-manager'],
      restrictedIntegrations: []
    }
  ];

  readonly architecturalRules: readonly ArchitecturalRule[] = [
    {
      category: 'boundary-enforcement',
      rule: 'GTD domain must maintain pure David Allen methodology without external productivity system contamination',
      rationale: 'Preserving the integrity of GTD methodology ensures consistent workflow behavior',
      exceptions: []
    },
    {
      category: 'aggregate-design',
      rule: 'GTD aggregates should align with natural workflow boundaries (Inbox, Project, Action)',
      rationale: 'Workflow boundaries provide natural consistency boundaries for aggregates',
      exceptions: []
    },
    {
      category: 'layer-separation',
      rule: 'GTD workflow rules belong in domain layer, not application services',
      rationale: 'David Allen methodology rules are core business logic that should be protected from infrastructure concerns',
      exceptions: []
    }
  ];

  readonly capabilities: readonly DomainCapability[] = [
    { name: 'captureInboxItem', description: 'Capture thoughts/inputs into trusted inbox (David Allen Step 1)', businessValue: 'Enables ubiquitous capture for stress-free productivity', technicalComplexity: 'low' },
    { name: 'clarifyInboxItem', description: 'Process inbox item through clarification workflow (David Allen Step 2)', businessValue: 'Transforms unclear inputs into actionable decisions', technicalComplexity: 'medium' },
    { name: 'organizeActions', description: 'Organize clarified items into appropriate lists (David Allen Step 3)', businessValue: 'Creates trusted system for tracking commitments', technicalComplexity: 'medium' },
    { name: 'completeAction', description: 'Mark next action as completed and update project status', businessValue: 'Maintains project momentum and progress visibility', technicalComplexity: 'low' },
    { name: 'createProject', description: 'Define multi-step project with desired outcome', businessValue: 'Provides clarity on multi-step outcomes', technicalComplexity: 'medium' },
    { name: 'defineNextAction', description: 'Specify concrete next physical action for project advancement', businessValue: 'Eliminates thinking about what to do next', technicalComplexity: 'low' },
    { name: 'scheduleReview', description: 'Set up weekly/daily review cycles (David Allen Step 4)', businessValue: 'Keeps system current and trusted', technicalComplexity: 'low' },
    { name: 'engageWithWork', description: 'Choose next action based on context, energy, time (David Allen Step 5)', businessValue: 'Optimizes productivity based on current resources', technicalComplexity: 'medium' },
    { name: 'getInboxItems', description: 'Retrieve unprocessed and processed inbox items', businessValue: 'Provides visibility into capture pipeline', technicalComplexity: 'low' },
    { name: 'getNextActions', description: 'List available next actions by context and energy level', businessValue: 'Enables context-appropriate action selection', technicalComplexity: 'medium' },
    { name: 'getProjectStatus', description: 'Check project progress and next action status', businessValue: 'Maintains awareness of commitments', technicalComplexity: 'low' },
    { name: 'validateWorkflow', description: 'Ensure GTD workflow integrity (2-minute rule, single next action)', businessValue: 'Maintains methodology consistency', technicalComplexity: 'high' }
  ];

  getNamingRules(): NamingRule[] {
    return [...this.namingRules];
  }

  getIntegrationRules(): IntegrationRule[] {
    return [...this.integrationRules];
  }

  getArchitecturalRules(): ArchitecturalRule[] {
    return [...this.architecturalRules];
  }

  getCapabilities(): DomainCapability[] {
    return [...this.capabilities];
  }

  getEventMetadata(): DomainEventMetadata[] {
    return [...this.events];
  }

  readonly events: readonly DomainEventMetadata[] = [
    { eventName: 'ItemCaptured', description: 'New item added to trusted inbox system', producedBy: 'InboxItem', consumedBy: ['ai-domain', 'party-manager'], schema: 'ItemCapturedEvent' },
    { eventName: 'ItemClarified', description: 'Inbox item processed through what-is-it decision tree', producedBy: 'InboxItem', consumedBy: ['project-management'], schema: 'ItemClarifiedEvent' },
    { eventName: 'ItemOrganized', description: 'Clarified item placed in appropriate GTD list', producedBy: 'InboxItem', consumedBy: ['project-management'], schema: 'ItemOrganizedEvent' },
    { eventName: 'ProjectCreated', description: 'Multi-step project defined with clear outcome', producedBy: 'Project', consumedBy: ['ai-domain', 'party-manager'], schema: 'ProjectCreatedEvent' },
    { eventName: 'ProjectCompleted', description: 'Project successfully finished and archived', producedBy: 'Project', consumedBy: ['party-manager'], schema: 'ProjectCompletedEvent' },
    { eventName: 'NextActionDefined', description: 'Concrete next physical action specified', producedBy: 'NextAction', consumedBy: ['ai-domain'], schema: 'NextActionDefinedEvent' },
    { eventName: 'ActionCompleted', description: 'Next action finished and project advanced', producedBy: 'NextAction', consumedBy: ['project-management', 'party-manager'], schema: 'ActionCompletedEvent' },
    { eventName: 'ReviewScheduled', description: 'Weekly review cycle established', producedBy: 'Review', consumedBy: ['ai-domain'], schema: 'ReviewScheduledEvent' },
    { eventName: 'ReviewCompleted', description: 'GTD review process finished with updated lists', producedBy: 'Review', consumedBy: ['party-manager'], schema: 'ReviewCompletedEvent' },
    { eventName: 'ContextAssigned', description: 'Action tagged with appropriate context (@calls, @computer)', producedBy: 'NextAction', consumedBy: ['ai-domain'], schema: 'ContextAssignedEvent' },
    { eventName: 'EnergyLevelSet', description: 'Action categorized by required energy level', producedBy: 'NextAction', consumedBy: ['ai-domain'], schema: 'EnergyLevelSetEvent' },
    { eventName: 'SomedayMaybeItemCreated', description: 'Future possibility captured for review', producedBy: 'SomedayMaybeItem', consumedBy: ['ai-domain'], schema: 'SomedayMaybeItemCreatedEvent' },
    { eventName: 'ReferenceItemStored', description: 'Information stored in reference system', producedBy: 'ReferenceItem', consumedBy: ['ai-domain'], schema: 'ReferenceItemStoredEvent' }
  ];

  validateNaming(pattern: string, name: string): { isValid: boolean; violations: string[]; suggestions: string[] } {
    const rule = this.namingRules.find(r => r.pattern === pattern);
    if (!rule) {
      return { isValid: true, violations: [], suggestions: [] };
    }

    const violations: string[] = [];
    const suggestions: string[] = [];

    // GTD-specific validation
    if (pattern === 'entity') {
      if (!this.isGTDTerm(name)) {
        violations.push(`Productivity concept "${name}" should use language from David Allen's Getting Things Done methodology`);
        suggestions.push('Use authentic GTD terms: InboxItem, Project, NextAction, Reference, SomedayMaybe');
      }
    }

    if (pattern === 'command') {
      if (!this.isGTDWorkflowVerb(name)) {
        violations.push(`Productivity action "${name}" should follow David Allen's 5-step GTD workflow verbs`);
        suggestions.push('Use GTD workflow actions: Capture, Clarify, Organize, Review, Engage');
      }
    }

    if (pattern === 'event') {
      if (!this.isPastTense(name)) {
        violations.push(`Productivity outcome "${name}" should reflect a completed GTD workflow step (facts that happened in your system)`);
        suggestions.push('Use GTD workflow facts: ItemCaptured, ProjectCompleted, ActionDefined');
      }
    }

    return {
      isValid: violations.length === 0,
      violations,
      suggestions
    };
  }

  validateIntegration(targetDomain: string, integrationType: string): { isValid: boolean; violations: string[]; recommendations: string[] } {
    const rule = this.integrationRules.find(r => 
      r.allowedIntegrations.includes(targetDomain) || r.allowedIntegrations.includes('*')
    );

    if (!rule) {
      return {
        isValid: false,
        violations: [`No integration rule defined for domain "${targetDomain}"`],
        recommendations: ['Define explicit integration strategy maintaining GTD methodology purity']
      };
    }

    // Special validation for GTD domain purity
    if (integrationType === 'direct-import') {
      return {
        isValid: false,
        violations: ['Direct imports violate GTD domain purity - external systems contaminate David Allen methodology'],
        recommendations: ['Use Published Language or Anti-Corruption Layer to maintain GTD workflow integrity']
      };
    }

    return { isValid: true, violations: [], recommendations: [] };
  }

  calculateHealthScore(): { score: number; breakdown: { naming: number; integration: number; architecture: number; capabilities: number } } {
    const namingScore = 100; // Follows GTD naming conventions
    const integrationScore = 100; // Proper Published Language with AI domain
    const architectureScore = 100; // Clean domain boundaries
    const capabilitiesScore = Math.min(100, (this.capabilities.length / 12) * 100); // 12 capabilities matches target

    const totalScore = Math.round((namingScore + integrationScore + architectureScore + capabilitiesScore) / 4);

    return {
      score: totalScore,
      breakdown: {
        naming: namingScore,
        integration: integrationScore,
        architecture: architectureScore,
        capabilities: capabilitiesScore
      }
    };
  }

  // Private helper methods for GTD domain validation
  private isGTDTerm(name: string): boolean {
    const gtdTerms = ['inbox', 'project', 'action', 'next', 'someday', 'maybe', 'reference', 'context', 'review', 'capture', 'clarify', 'organize', 'engage'];
    return gtdTerms.some(term => name.toLowerCase().includes(term));
  }

  private isGTDWorkflowVerb(name: string): boolean {
    const gtdVerbs = ['capture', 'clarify', 'organize', 'review', 'engage', 'complete', 'create', 'define', 'process', 'schedule'];
    return gtdVerbs.some(verb => name.toLowerCase().startsWith(verb));
  }

  private isPastTense(name: string): boolean {
    // Handle kebab-case events like 'item-captured'
    if (name.includes('-')) {
      const parts = name.split('-');
      const lastPart = parts[parts.length - 1];
      return this.endsWithPastTense(lastPart);
    }
    
    // Handle PascalCase events like 'ItemCaptured'
    return this.endsWithPastTense(name.toLowerCase());
  }

  private endsWithPastTense(word: string): boolean {
    return word.endsWith('ed') || word.endsWith('d') || 
           ['captured', 'clarified', 'organized', 'completed', 'created', 'defined', 'assigned', 'stored', 'scheduled'].some(past => word.endsWith(past));
  }
}

/**
 * Factory function for domain rules discovery
 */
export function createGTDDomainRules(): GTDDomainRules {
  return new GTDDomainRules();
}